import { DisplayDictionary, DisplayValue } from "../../../.."
import { Button, CircularProgress } from "@mui/material"
import Field from "../../fields/Field"
import { SelectOption } from "../../fields/SelectField"
import React from "react"

// The frontend can set the investigation extent and other enquiry settings when option 1 or 2 is selected

// There are all the available settings. If under a sponsor then not all will be available.
// "investigation_extent"
// "report_cp_order"
// "report_include_provisions"
// "fast_forward"
// "all_cp_in_profile"
// "assess_parent_immunity"
// "one_only"
// "assess_ukefc"
// "cs_eval_all_reqts"
// "screen"
// "assess_snt"
// "max_generations"
// "report_include_memo"

// If option 1 is chosen, then these enquiry settings should be chosen:
const naturalisationSettings = {
    "investigation_extent": "P2",
    "fast_forward": "false",
    "cs_eval_all_reqts": "true",
    "all_cp_in_profile": "false",
    "report_cp_order": "S",
    "report_include_memo": "true"
}

// If option 2 is chosen, then these enquiry settings should be chosen:
const complexSettings = {
    "investigation_extent": "A",
    "fast_forward": "true",
    "cs_eval_all_reqts": "true",
    "all_cp_in_profile": "true",
    "report_cp_order": "S",
    "report_include_memo": "true"
}

const noAssessmentMethodStatuses = ["UKCS_SW_J", "UKLLR_SW_P", "UKLLR_SNR_P", "UKLLR_GTR_P", "UKLLR_UKX_P"]
const exclusiveStatuses = ["UKROA2", "UKEFC", "UKCS_SW_J", "UKLLR_SW_P", "UKLLR_SNR_P", "UKLLR_GTR_P", "UKLLR_UKX_P"]

const assessmentMethodOptions = [
    {
        value: "naturalisation", 
        label: "Recommended settings for naturalisation or minors",
        help: "Quickest method, but limits the assessment to certain routes."
    },
    {
        value: "complex", 
        label: "Recommended settings for complex claims",
        help: "A comprehensive method that evaluates all possible routes, including ancestry, to citizenship."
    }
]

function getStatusOptionsFromStatusSection (statusNodes) {

    if (!statusNodes) return []

    // Transform flat list of statuses into a tree structured by type
    var nationalityOptionsNested = []
    // TODO: are we sure this list includes all possible categories? Can we avoid hard-coding it?
    var typeOrder = ["SW", "GBM", "CITIZEN", "PROTÉGÉ", "PR_STATUS", "TR_STATUS"]

    // Add each status to tree
    typeOrder.forEach((category => {

        var statuses = statusNodes.filter(nat => category.includes(nat.category))

        if (category === "PR_STATUS") {
            statuses = statuses.reverse()
        }

        if (statuses?.length) {
            nationalityOptionsNested.push({
                label: DisplayValue("status_tree_category", category),
                value: category,
                children: statuses.map(status => {
                    return {
                        value: status.code,
                        label: status.label,
                        // disabled: disabled
                    }
                })
            })
        }
    }))

    return nationalityOptionsNested
}


export default function EnquiryStatusesFormSection ({formik, enquiry, setEnquiry, defaultSettings}) {

    const [assessmentMethod, setAssessmentMethod] = React.useState("default")
    const statusOptions: SelectOption[] = getStatusOptionsFromStatusSection(enquiry?.statuses)

    React.useEffect(() => {
    
        if (!assessmentMethod) return
        
        var settings = enquiry?.settings?.map(setting => {
            if (assessmentMethod === "naturalisation" && setting.code in naturalisationSettings) {
                return {...setting, value: naturalisationSettings[setting.code]}
            }
            if (assessmentMethod === "complex" && setting.code in complexSettings) {
                return {...setting, value: complexSettings[setting.code]}
            }
            if (defaultSettings && assessmentMethod === "default" && setting.code in defaultSettings) {
                return {...setting, value: defaultSettings[setting.code]}
            }
            return setting
        })

        setEnquiry({...enquiry, settings: settings})

    }, [assessmentMethod])

    return (
        <div>
            <h4>Select the status you would like to evaluate</h4>
            
            <div style={{maxHeight: "40vh", overflowY: "auto"}}>
                {statusOptions.map((optionSet, i) => (
                    <div style={{padding: "10px 12px", display: "grid", gap: "4px"}}>
                        <label>{optionSet.label}</label>
                        
                        {optionSet.children?.map((option, i) => (
                            <Field.Checkbox
                                formik
                                label={option.label}
                                value={formik.values.statuses.includes(option.value)}
                                warning={false}
                                onChange={(e) => {
                                    
                                    var value = formik.values.statuses.includes(option.value)
                                        ? formik.values.statuses.filter(v => v !== option.value)
                                        : [...formik.values.statuses, option.value]
                                        
                                    const ukbcStatus = value.find((status) => status === "UKBC")
                                    const existingExclusiveStatus = formik.values.statuses.find((status) => exclusiveStatuses.includes(status))
                                    const newExclusiveStatus = !existingExclusiveStatus && value.find((status) => exclusiveStatuses.includes(status)) 

                                    // Handle exclusive statuses
                                    if (newExclusiveStatus) {
                                        value = [newExclusiveStatus]
                                    }
                                    if (existingExclusiveStatus && value.length > 1) {
                                        value = value.filter(v => v !== existingExclusiveStatus)
                                    }
                                    if (value.find(status => noAssessmentMethodStatuses.includes(status))) {
                                        setAssessmentMethod("default")
                                    }
                                    // Auto-select assessment method
                                    if (!ukbcStatus && assessmentMethod === "naturalisation") {
                                        setAssessmentMethod("default")
                                    }
                                    // if "UKEFC" is selected, make assess_ukefc setting = 'true'
                                    if (value.find((status) => status === "UKEFC")) {
                                        var settings = enquiry.settings.map((setting) => {
                                            if (setting.uid === "assess_ukefc") {
                                                return {...setting, value: "true"}
                                            }
                                            return setting
                                        })

                                        setEnquiry({...enquiry, settings: settings})
                                    }

                                    formik.setFieldValue("statuses", value)
                                }} />
                        ))}
                    </div>
                ))}
            </div>

            {formik.submitCount && formik.errors.statuses ? (
                <div className="warning">{formik.errors.statuses.toString()}</div>
            ) : null}

            {formik.values.statuses?.length && !formik.values.statuses.find(status => noAssessmentMethodStatuses.includes(status)) ? (
                <div style={{display: "grid", gap: "10px", marginTop: "20px"}}>
                    <h4>Assessment method</h4>

                    <Field.Radio
                        formik
                        value={assessmentMethod}
                        warning={false}
                        options={formik.values.statuses?.includes("UKBC") 
                            ? assessmentMethodOptions 
                            : assessmentMethodOptions.filter(option => option.value !== "naturalisation")}
                        onChange={(e) => setAssessmentMethod(e.target.value)}
                        />
                </div>
            ) : null}
        </div>
    )
}